import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { Switch } from '../common/Switch';
import { Order } from '../../types/order';
import { InventoryMovementType, InventoryReport } from '../../types/warehouse';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../hooks/useProducts';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useInventory } from '../../hooks/useInventory';
import { DocumentChainService } from '../../services/documentChainService';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { TruckIcon, CubeIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon, CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onCreated?: (deliveryNoteId: string) => void;
}

type Kind = 'product' | 'service' | 'unknown';

interface Row {
  idx: number;
  productId: string;
  name: string;
  code?: string;
  ordered: number;
  deliveredBefore: number;
  now: number;                  // most szállított mennyiség
  kind: Kind;
  warehouseId: string;
  warehouses: { id: string; name: string; quantity: number; reservedForOrder: number }[];
  deduct: boolean;              // készletlevonás
}

const OrderToDeliveryNoteModal: React.FC<Props> = ({ isOpen, onClose, order, onCreated }) => {
  const { user } = useAuth();
  const { products } = useProducts();
  const { warehouses } = useWarehouses();
  const { getInventoryReport, createInventoryMovement } = useInventory();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen || !order) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotes('');
      const items = order.items || [];
      const base: Row[] = items.map((it, idx) => {
        const p = products.find(x => x.id === it.productId);
        const kind: Kind = !p ? 'unknown' : (p.type === 'service' || p.isService) ? 'service' : 'product';
        const before = Number(it.deliveredQuantity) || 0;
        const remaining = Math.max(0, (Number(it.quantity) || 0) - before);
        return { idx, productId: it.productId || '', name: it.productName, code: it.productCode, ordered: Number(it.quantity) || 0, deliveredBefore: before, now: remaining, kind, warehouseId: '', warehouses: [], deduct: kind === 'product' && remaining > 0 };
      });
      const productIds = base.filter(r => r.kind === 'product' && r.productId).map(r => r.productId);
      let report: InventoryReport[] = [];
      // a megrendeléshez tartozó foglalások (raktáranként)
      const reservedMap = new Map<string, number>(); // `${productId}|${warehouseId}` -> nettó foglalt
      if (productIds.length) {
        try { report = await getInventoryReport(productIds); } catch (e) { console.error(e); }
        try {
          const snap = await getDocs(query(collection(db, 'inventoryMovements'), where('organizationId', '==', order.organizationId), where('documentId', '==', order.id)));
          snap.docs.forEach(d => {
            const m = d.data() as any;
            if (m.isActive === false) return;
            const k = `${m.productId}|${m.warehouseId}`;
            const sign = m.type === InventoryMovementType.STOCK_RESERVATION ? 1 : m.type === InventoryMovementType.STOCK_RESERVATION_RELEASE ? -1 : 0;
            if (sign) reservedMap.set(k, (reservedMap.get(k) || 0) + sign * (Number(m.quantity) || 0));
          });
        } catch (e) { console.error('reservation lookup failed', e); }
      }
      if (cancelled) return;
      setRows(base.map(r => {
        if (r.kind !== 'product') return r;
        const rep = report.find(x => x.productId === r.productId);
        const whs = warehouses.map(w => ({
          id: w.id, name: w.name,
          quantity: rep?.warehouseDetails.find(x => x.warehouseId === w.id)?.quantity || 0,
          reservedForOrder: Math.max(0, reservedMap.get(`${r.productId}|${w.id}`) || 0)
        }));
        // alapértelmezett raktár: ahol foglalás van, különben ahol a legtöbb készlet
        const best = [...whs].sort((a, b) => (b.reservedForOrder - a.reservedForOrder) || (b.quantity - a.quantity))[0];
        return { ...r, warehouses: whs, warehouseId: best?.id || '', deduct: r.deduct && !!best };
      }));
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order?.id, products.length, warehouses.length]);

  const update = (idx: number, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.idx === idx ? { ...r, ...patch } : r)));

  const problems = useMemo(() => rows.filter(r => {
    if (r.kind !== 'product' || !r.deduct || r.now <= 0) return false;
    const wh = r.warehouses.find(w => w.id === r.warehouseId);
    return (wh?.quantity || 0) < r.now;
  }), [rows]);
  const nothingToDeliver = rows.every(r => r.now <= 0);

  if (!order) return null;

  const handleCreate = async () => {
    if (!user?.organizationId) return;
    if (nothingToDeliver) { toast.error('Nincs szállítandó mennyiség'); return; }
    try {
      setBusy(true);
      const uid = (user as any).id || (user as any).uid;
      const items = order.items.map((it, idx) => {
        const r = rows.find(x => x.idx === idx)!;
        return {
          productId: it.productId, productName: it.productName, productCode: it.productCode,
          orderedQuantity: r.ordered, deliveredQuantity: r.now,
          unitPrice: it.unitPrice, price: it.price, taxRate: it.taxRate, discount: it.discount,
          lineTotal: r.ordered ? Number(((it.lineTotal || 0) * r.now / r.ordered).toFixed(2)) : 0,
          lineTotalWithTax: r.ordered ? Number(((it.lineTotalWithTax || 0) * r.now / r.ordered).toFixed(2)) : 0,
          currency: it.currency, notes: it.notes || ''
        };
      }).filter(i => i.deliveredQuantity > 0);
      const subtotal = items.reduce((s, i) => s + i.lineTotal, 0);
      const total = items.reduce((s, i) => s + i.lineTotalWithTax, 0);
      const fullyDelivered = rows.every(r => r.deliveredBefore + r.now >= r.ordered);

      const note = await DocumentChainService.createDeliveryNoteFromOrder(order, user.organizationId, uid, {
        items: items as any, deliveryDate, notes,
        subtotal, taxAmount: total - subtotal, totalAmount: total,
        status: 'shipped'
      });

      // Készletmozgások
      let out = 0, failed = 0;
      for (const r of rows.filter(x => x.kind === 'product' && x.deduct && x.now > 0 && x.warehouseId)) {
        const wh = r.warehouses.find(w => w.id === r.warehouseId);
        const release = Math.min(r.now, wh?.reservedForOrder || 0);
        try {
          if (release > 0) {
            await createInventoryMovement({
              productId: r.productId, warehouseId: r.warehouseId, quantity: release,
              type: InventoryMovementType.STOCK_RESERVATION_RELEASE,
              documentId: order.id, documentType: 'order', documentNumber: order.number,
              note: `Foglalás feloldása – szállítólevél ${note.number}`
            });
          }
          await createInventoryMovement({
            productId: r.productId, warehouseId: r.warehouseId, quantity: r.now,
            type: InventoryMovementType.STOCK_OUT,
            documentId: note.id, documentType: 'delivery_note', documentNumber: note.number,
            note: `Kiadás – szállítólevél ${note.number} (megrendelés ${order.number})`
          });
          out++;
        } catch (e: any) { console.error('stock out failed', e); failed++; }
      }

      // Megrendelés tételeinek szállított mennyisége + státusz
      try {
        const updatedItems = order.items.map((it, idx) => {
          const r = rows.find(x => x.idx === idx)!;
          return { ...it, deliveredQuantity: r.deliveredBefore + r.now, deliveryDate: r.now > 0 ? deliveryDate : it.deliveryDate };
        });
        await updateDoc(doc(db, 'orders', order.id), {
          items: updatedItems.map(i => Object.fromEntries(Object.entries(i).filter(([, v]) => v !== undefined))),
          status: fullyDelivered ? 'shipped' : order.status === 'draft' || order.status === 'pending' ? 'confirmed' : order.status,
          updatedAt: Timestamp.now()
        });
      } catch (e) { console.error('order update failed', e); }

      const parts = [`Szállítólevél létrehozva: ${note.number}`];
      if (out) parts.push(`${out} tétel készletből kiadva`);
      if (!fullyDelivered) parts.push('részteljesítés');
      toast.success(parts.join(' · '));
      if (failed) toast.error(`${failed} tétel készletlevonása nem sikerült (nincs elég készlet) – ellenőrizd a raktárban`);
      onCreated?.(note.id);
      onClose();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'A szállítólevél létrehozása nem sikerült');
    } finally {
      setBusy(false);
    }
  };

  const KindBadge: React.FC<{ kind: Kind }> = ({ kind }) => (
    kind === 'product' ? <span className="inline-flex items-center gap-1 text-[11px] text-sky-700 dark:text-sky-300"><CubeIcon className="w-3.5 h-3.5" />Termék</span>
      : kind === 'service' ? <span className="inline-flex items-center gap-1 text-[11px] text-violet-700 dark:text-violet-300"><WrenchScrewdriverIcon className="w-3.5 h-3.5" />Szolgáltatás</span>
        : <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><QuestionMarkCircleIcon className="w-3.5 h-3.5" />Egyedi tétel</span>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Szállítólevél készítése" size="3xl" noPadding>
      <div className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)]">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">Megrendelés {order.number}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{order.customerName}{order.deliveryAddress ? ` · ${order.deliveryAddress}` : ''}</div>
        </div>

        <div className="flex-1 p-4 sm:p-6 space-y-4">
          <div className="crm-card overflow-hidden">
            <div className="px-3 py-2 border-b border-[var(--crm-border)] text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center justify-between">
              <span>Tételek ({rows.length})</span>
              {loading && <span className="normal-case font-normal">Készlet betöltése…</span>}
            </div>
            <ul className="divide-y divide-[var(--crm-border)]">
              {rows.map(r => {
                const wh = r.warehouses.find(w => w.id === r.warehouseId);
                const remaining = Math.max(0, r.ordered - r.deliveredBefore);
                const short = r.kind === 'product' && r.deduct && r.now > 0 && (wh?.quantity || 0) < r.now;
                return (
                  <li key={r.idx} className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{r.name}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                        <KindBadge kind={r.kind} />
                        <span>Rendelt: {r.ordered}</span>
                        {r.deliveredBefore > 0 && <span>Korábban szállítva: {r.deliveredBefore}</span>}
                        {r.kind === 'product' && !loading && wh && (
                          <span className={short ? 'text-amber-600 dark:text-amber-400' : ''}>Készlet ({wh.name}): {wh.quantity}{wh.reservedForOrder ? ` · foglalt ehhez: ${wh.reservedForOrder}` : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label className="text-[11px] text-gray-500 dark:text-gray-400">Most:</label>
                      <input type="number" min={0} max={remaining} step="any" value={r.now} onChange={e => update(r.idx, { now: Math.max(0, Math.min(remaining, parseFloat(e.target.value) || 0)) })} className="w-20 h-8 border rounded-lg px-2 text-sm text-right tabular-nums" />
                      {r.kind === 'product' && (
                        <>
                          <select value={r.warehouseId} onChange={e => update(r.idx, { warehouseId: e.target.value })} className="h-8 pl-2 pr-7 text-xs border rounded-lg max-w-[160px]" disabled={!r.warehouses.length}>
                            {r.warehouses.length === 0 && <option value="">Nincs raktár</option>}
                            {r.warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.quantity})</option>)}
                          </select>
                          <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap">
                            <Switch checked={r.deduct} onChange={(v) => update(r.idx, { deduct: v })} size="sm" />Levonás
                          </label>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {problems.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>Nincs elég készlet a választott raktárban: {problems.map(p => p.name).join(', ')}. A levonás ezeknél sikertelen lesz – válassz másik raktárt, csökkentsd a mennyiséget vagy kapcsold ki a levonást.</div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Szállítás dátuma</label>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="w-full h-9 border rounded-lg px-3 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Megjegyzés a szállítólevélre</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>A szállítólevél „Kiszállítva” státuszban jön létre. Termékeknél a „Most” mennyiség kikerül a készletből, a megrendeléshez tartozó foglalás feloldódik. Ha nem minden tétel megy ki, részteljesítés lesz, és később újabb szállítólevél készíthető a maradékra.</span>
          </div>
        </div>

        <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto" disabled={busy}>Mégse</button>
          <button type="button" onClick={handleCreate} disabled={busy || loading || nothingToDeliver} className="crm-btn crm-btn-primary">
            <TruckIcon className="w-4 h-4 mr-1.5" />{busy ? 'Létrehozás…' : 'Szállítólevél létrehozása'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderToDeliveryNoteModal;
